// Section 1: Basic Project Information
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Calculator, Layers, ParkingSquare } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";
import { REQ } from "../utils";

export const BasicInfoSection = ({
  formData,
  setFormData,
  handleChange,
  handleSelectChange,
  getInputClass,
  hasFieldError,
  fsiBreakdown,
  plData,
  constructionCosts
}) => {
  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-sky-400" data-testid="quick-basic-info">
      <CardHeader className="pb-3 bg-sky-100 rounded-t-xl border-b border-sky-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-sky-900">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          1. Basic Project Information
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Project Name */}
        <div className="space-y-2">
          <Label htmlFor="q-projectName">Project / Society Name *</Label>
          <Input
            id="q-projectName"
            name="projectName"
            value={formData.projectName}
            onChange={handleChange}
            placeholder="Enter project name"
            className={REQ}
            data-testid="quick-project-name"
          />
        </div>
        
        {/* Location */}
        <div className="space-y-2">
          <Label htmlFor="q-location">Location *</Label>
          <Input
            id="q-location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="City, Area"
            className={REQ}
            data-testid="quick-location"
          />
        </div>
        
        {/* Project Mode */}
        <div className="space-y-2">
          <Label>Project Mode</Label>
          <Select
            value={formData.projectMode}
            onValueChange={(value) => handleSelectChange("projectMode", value)}
          >
            <SelectTrigger className="rounded-sm" data-testid="quick-mode">
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="redevelopment">Redevelopment</SelectItem>
              <SelectItem value="greenfield">Greenfield (JV)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Plot Area */}
        <div className="space-y-2">
          <Label htmlFor="q-plotAreaPerCard">Plot Area (Sq Mtr) *</Label>
          <IndianNumberInput
            id="q-plotAreaPerCard"
            name="plotAreaPerCard"
            value={formData.plotAreaPerCard}
            onChange={handleChange}
            placeholder="0"
            className={getInputClass("plotAreaPerCard", REQ)}
            data-testid="quick-plot-area"
          />
          {hasFieldError("plotAreaPerCard") && <p className="text-xs text-red-500">Required</p>}
        </div>
        
        {/* Road Width */}
        <div className="space-y-2">
          <Label htmlFor="q-roadWidth">Road Width (Meters) *</Label>
          <Input
            id="q-roadWidth"
            name="roadWidth"
            type="number"
            value={formData.roadWidth}
            onChange={handleChange}
            placeholder="0"
            className={getInputClass("roadWidth", REQ)}
            data-testid="quick-road-width"
          />
          {hasFieldError("roadWidth") && <p className="text-xs text-red-500">Required</p>}
        </div>
        
        {/* Project Classification */}
        <div className="space-y-2">
          <Label>Project Classification</Label>
          <Select
            value={formData.projectClassification}
            onValueChange={(value) => {
              // CBD is compulsorily commercial — force projectType to "commercial"
              if (value === "cbd") {
                setFormData({
                  ...formData,
                  projectClassification: value,
                  projectType: "commercial",
                  residentialPercentage: "0",
                  commercialPercentage: "100",
                });
              } else {
                handleSelectChange("projectClassification", value);
              }
            }}
          >
            <SelectTrigger className="rounded-sm" data-testid="quick-classification">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="non-tod">Non-TOD</SelectItem>
              <SelectItem value="tod">TOD</SelectItem>
              <SelectItem value="mhada">MHADA</SelectItem>
              <SelectItem value="cbd">CBD (Commercial)</SelectItem>
            </SelectContent>
          </Select>
          {formData.projectClassification === "cbd" && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              CBD zone — compulsorily commercial. FSI by road: 12–18m → 3, 18–27m → 4, 27m+ → 5. Premium FSI above baseline is chargeable at <strong>50% of land RR rate</strong>.
            </p>
          )}
        </div>
        
        {/* MHADA-specific: Titbit Area */}
        {formData.projectClassification === "mhada" && (
          <>
            <div className="space-y-2">
              <Label>Titbit Area Available?</Label>
              <Select
                value={formData.hasTitbitArea || "no"}
                onValueChange={(value) => handleSelectChange("hasTitbitArea", value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-has-titbit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.hasTitbitArea === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="q-titbitArea">Titbit Area (Sq Mtr)</Label>
                <IndianNumberInput
                  id="q-titbitArea"
                  name="titbitArea"
                  value={formData.titbitArea}
                  onChange={handleChange}
                  placeholder="500"
                  className="rounded-sm"
                  data-testid="quick-titbit-area"
                />
                <p className="text-xs text-muted-foreground">Added to Plot Area for total FSI calculation</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="q-existingBuiltupArea">Existing Built-up Area (Sq Mtr)</Label>
              <IndianNumberInput
                id="q-existingBuiltupArea"
                name="existingBuiltupArea"
                value={formData.existingBuiltupArea}
                onChange={handleChange}
                placeholder="1000"
                className="rounded-sm"
                data-testid="quick-existing-builtup-area"
              />
              <p className="text-xs text-muted-foreground">MHADA Premium FSI = (Plot + Titbit) × 3 − Existing Built-up</p>
            </div>
          </>
        )}
        
        {/* ULC Section 20 - Redevelopment only */}
        {formData.projectMode !== "greenfield" && formData.projectClassification !== "mhada" && (
          <>
            <div className="space-y-2">
              <Label>Sanctioned under Sec 20 of ULC?</Label>
              <Select
                value={formData.ulcSection20 || "no"}
                onValueChange={(value) => handleSelectChange("ulcSection20", value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-ulc-section20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.ulcSection20 === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="q-ulcArea">Area under ULC (Sq Mtr)</Label>
                <IndianNumberInput
                  id="q-ulcArea"
                  name="ulcArea"
                  value={formData.ulcArea}
                  onChange={handleChange}
                  placeholder="500"
                  className="rounded-sm"
                  data-testid="quick-ulc-area"
                />
                <p className="text-xs text-muted-foreground">Cost = ULC Area × Land RR × 2.5%</p>
              </div>
            )}
          </>
        )}
        
        {/* Year of Construction & TDR - Non-MHADA Redevelopment */}
        {formData.projectMode !== "greenfield" && formData.projectClassification !== "mhada" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="q-completionYear">Year of Construction</Label>
              <Input
                id="q-completionYear"
                name="completionYear"
                type="number"
                value={formData.completionYear}
                onChange={handleChange}
                placeholder={`e.g., ${new Date().getFullYear() - 35}`}
                className="rounded-sm"
                data-testid="quick-completion-year"
              />
              <p className="text-xs text-muted-foreground">
                {formData.completionYear && (new Date().getFullYear() - parseInt(formData.completionYear)) > 30 
                  ? <span className="text-green-600">✓ Building 30+ years old - eligible for 30% Incentive FSI</span>
                  : "Enter year to check eligibility for 30% Incentive FSI (building must be 30+ years old)"
                }
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-existingBuiltupAreaNonMhada">Existing Built-up Area (Sq Mtr)</Label>
              <IndianNumberInput
                id="q-existingBuiltupAreaNonMhada"
                name="existingBuiltupArea"
                value={formData.existingBuiltupArea}
                onChange={handleChange}
                placeholder="0"
                className="rounded-sm"
                data-testid="quick-existing-builtup-nonmhada"
              />
              <p className="text-xs text-muted-foreground">
                Used for Incentive FSI calculation. If blank, uses Plot Area.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Is any TDR already used?</Label>
              <Select
                value={formData.hasExistingTDR || "no"}
                onValueChange={(value) => {
                  const newData = { ...formData, hasExistingTDR: value };
                  if (value === "no") {
                    newData.existingTDRArea = "0";
                  }
                  setFormData(newData);
                }}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-has-existing-tdr">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.hasExistingTDR === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="q-existingTDRArea">TDR Area Already Used (Sq Mtr)</Label>
                <IndianNumberInput
                  id="q-existingTDRArea"
                  name="existingTDRArea"
                  value={formData.existingTDRArea}
                  onChange={handleChange}
                  placeholder="0"
                  className="rounded-sm"
                  data-testid="quick-existing-tdr-area"
                />
                <p className="text-xs text-muted-foreground">
                  This TDR will be deducted from the TDR to be purchased
                </p>
              </div>
            )}
          </>
        )}
        
        {/* Number of Members - Hidden for Greenfield */}
        {formData.projectMode !== "greenfield" && (
          <div className="space-y-2">
            <Label htmlFor="q-numMembers">Number of Members *</Label>
            <Input
              id="q-numMembers"
              name="numMembers"
              type="number"
              value={formData.numMembers}
              onChange={handleChange}
              placeholder="0"
              className={REQ}
              data-testid="quick-num-members"
            />
          </div>
        )}
        
        {/* Building Height */}
        <div className="space-y-2">
          <Label>Building Height</Label>
          <Select
            value={formData.buildingHeight}
            onValueChange={(value) => {
              const carpetFactorMap = { "24": "87", "36": "85", "45": "82", "70": "82" };
              setFormData({
                ...formData,
                buildingHeight: value,
                constructionCostPerSqft: String(constructionCosts[value] || formData.constructionCostPerSqft),
                carpetAreaFactor: carpetFactorMap[value] || formData.carpetAreaFactor
              });
            }}
          >
            <SelectTrigger className="rounded-sm" data-testid="quick-height">
              <SelectValue placeholder="Select height" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24">24 metres</SelectItem>
              <SelectItem value="36">36 metres</SelectItem>
              <SelectItem value="45">45 metres</SelectItem>
              <SelectItem value="70">70 metres</SelectItem>
              <SelectItem value="100">100 metres +</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ========== RECREATIONAL FLOOR ========== */}
        <div className="md:col-span-2 lg:col-span-4 space-y-3 p-3 bg-purple-50/50 border border-purple-200 rounded-sm">
          <div className="flex items-center gap-4">
            <Layers className="h-4 w-4 text-purple-600" />
            <Label className="font-medium text-sm">Recreational Floor</Label>
            <div className="flex gap-4 ml-auto">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasRecreationalFloor" value="no"
                  checked={formData.hasRecreationalFloor !== "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasRecreationalFloor: "no" }))}
                  className="h-3.5 w-3.5" data-testid="rec-floor-no" />
                No
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasRecreationalFloor" value="yes"
                  checked={formData.hasRecreationalFloor === "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasRecreationalFloor: "yes" }))}
                  className="h-3.5 w-3.5" data-testid="rec-floor-yes" />
                Yes
              </label>
            </div>
          </div>
          {formData.hasRecreationalFloor === "yes" && (
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Area of Recreational Floor (sft)</Label>
                <IndianNumberInput name="recreationalFloorAreaSqft" value={formData.recreationalFloorAreaSqft || ""}
                  onChange={handleChange} placeholder="e.g. 5,000" className="rounded-sm h-8 text-xs"
                  data-testid="rec-floor-area" />
              </div>
              <div className="p-2 bg-white rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Construction Cost/sft</p>
                <p className="font-mono font-semibold">₹{parseInt(formData.constructionCostPerSqft || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Recreational Floor Cost</p>
                <p className="font-mono font-semibold text-purple-700">
                  ₹{((parseFloat(formData.recreationalFloorAreaSqft) || 0) * (parseFloat(formData.constructionCostPerSqft) || 0)).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ========== BASEMENTS ========== */}
        <div className="md:col-span-2 lg:col-span-4 space-y-3 p-3 bg-amber-50/50 border border-amber-200 rounded-sm">
          <div className="flex items-center gap-4">
            <Layers className="h-4 w-4 text-amber-600" />
            <Label className="font-medium text-sm">Basements</Label>
            <div className="flex gap-4 ml-auto">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasBasements" value="no"
                  checked={formData.hasBasements !== "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasBasements: "no" }))}
                  className="h-3.5 w-3.5" data-testid="basement-no" />
                No
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasBasements" value="yes"
                  checked={formData.hasBasements === "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasBasements: "yes" }))}
                  className="h-3.5 w-3.5" data-testid="basement-yes" />
                Yes
              </label>
            </div>
          </div>
          {formData.hasBasements === "yes" && (
            <div className="grid md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">No. of Basements</Label>
                <Input type="number" name="numBasements" value={formData.numBasements || ""}
                  onChange={handleChange} placeholder="e.g. 2" className="rounded-sm h-8 text-xs"
                  data-testid="num-basements" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Area per Floor (sft)</Label>
                <IndianNumberInput name="basementAreaPerFloorSqft" value={formData.basementAreaPerFloorSqft || ""}
                  onChange={handleChange} placeholder="e.g. 10,000" className="rounded-sm h-8 text-xs"
                  data-testid="basement-area" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cost per sft (₹)</Label>
                <IndianNumberInput name="basementCostPerSqft" value={formData.basementCostPerSqft || ""}
                  onChange={handleChange} placeholder="e.g. 1,500" className="rounded-sm h-8 text-xs"
                  data-testid="basement-cost" />
              </div>
              <div className="p-2 bg-amber-100 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Total Basement Cost</p>
                <p className="font-mono font-semibold text-amber-700">
                  ₹{((parseFloat(formData.numBasements) || 0) * (parseFloat(formData.basementAreaPerFloorSqft) || 0) * (parseFloat(formData.basementCostPerSqft) || 0)).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ========== ADDITIONAL PARKING FLOORS ========== */}
        <div className="md:col-span-2 lg:col-span-4 space-y-3 p-3 bg-blue-50/50 border border-blue-200 rounded-sm">
          <div className="flex items-center gap-4">
            <ParkingSquare className="h-4 w-4 text-blue-600" />
            <Label className="font-medium text-sm">Additional Parking Floors (above parking level)</Label>
            <div className="flex gap-4 ml-auto">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasAdditionalParkingFloors" value="no"
                  checked={formData.hasAdditionalParkingFloors !== "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasAdditionalParkingFloors: "no" }))}
                  className="h-3.5 w-3.5" data-testid="addl-parking-no" />
                No
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="hasAdditionalParkingFloors" value="yes"
                  checked={formData.hasAdditionalParkingFloors === "yes"}
                  onChange={() => setFormData(prev => ({ ...prev, hasAdditionalParkingFloors: "yes" }))}
                  className="h-3.5 w-3.5" data-testid="addl-parking-yes" />
                Yes
              </label>
            </div>
          </div>
          {formData.hasAdditionalParkingFloors === "yes" && (
            <div className="grid md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">No. of Floors</Label>
                <Input type="number" name="numAdditionalParkingFloors" value={formData.numAdditionalParkingFloors || ""}
                  onChange={handleChange} placeholder="e.g. 2" className="rounded-sm h-8 text-xs"
                  data-testid="addl-parking-floors" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Area per Floor (sft)</Label>
                <IndianNumberInput name="additionalParkingFloorAreaSqft" value={formData.additionalParkingFloorAreaSqft || ""}
                  onChange={handleChange} placeholder="e.g. 8,000" className="rounded-sm h-8 text-xs"
                  data-testid="addl-parking-area" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cost per sft (₹)</Label>
                <IndianNumberInput name="additionalParkingCostPerSqft" value={formData.additionalParkingCostPerSqft || ""}
                  onChange={handleChange} placeholder="e.g. 1,200" className="rounded-sm h-8 text-xs"
                  data-testid="addl-parking-cost" />
              </div>
              <div className="p-2 bg-blue-100 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Total Parking Floor Cost</p>
                <p className="font-mono font-semibold text-blue-700">
                  ₹{((parseFloat(formData.numAdditionalParkingFloors) || 0) * (parseFloat(formData.additionalParkingFloorAreaSqft) || 0) * (parseFloat(formData.additionalParkingCostPerSqft) || 0)).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Project Type */}
        <div className="space-y-2">
          <Label>Project Type</Label>
          <Select
            value={formData.projectType}
            onValueChange={(value) => {
              let newData = { ...formData, projectType: value };
              if (value === "residential") {
                newData.residentialPercentage = "100";
                newData.commercialPercentage = "0";
              } else if (value === "commercial") {
                newData.residentialPercentage = "0";
                newData.commercialPercentage = "100";
              }
              setFormData(newData);
            }}
          >
            <SelectTrigger className="rounded-sm" data-testid="quick-project-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="mixed">Mixed Use</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Mixed Use Split */}
        {formData.projectType === "mixed" && (
          <div className="space-y-2">
            <Label>Residential %: {formData.residentialPercentage} / Commercial %: {formData.commercialPercentage}</Label>
            <Input
              type="number"
              value={formData.residentialPercentage}
              onChange={(e) => {
                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                setFormData({ ...formData, residentialPercentage: String(val), commercialPercentage: String(100 - val) });
              }}
              placeholder="60"
              className="rounded-sm"
              data-testid="quick-residential-pct"
            />
            <p className="text-xs text-muted-foreground">Enter residential %, commercial auto-calculated</p>
          </div>
        )}

        {/* Saleable Area Split (Offices + Shops) — commercial / mixed only. Offices + Shops must = 100% */}
        {(formData.projectType === "commercial" || formData.projectType === "mixed") && (() => {
          const offices = parseFloat(formData.saleableAreaSplitOffices) || 0;
          const shops = parseFloat(formData.saleableAreaSplitShops) || 0;
          const commercialSplit = offices + shops;
          const isValid = Math.abs(commercialSplit - 100) < 0.01;
          const isEmpty = commercialSplit === 0;
          // Total commercial saleable area available live — sum of offices + shops from plData
          const commercialSaleableTotal = ((plData?.saleableAreaOffices || 0) + (plData?.saleableAreaShops || 0));
          const officesArea = commercialSaleableTotal * (offices / 100);
          const shopsArea = commercialSaleableTotal * (shops / 100);

          // Reverse calc: user types area → back-calculate %
          const handleOfficesAreaChange = (val) => {
            const areaNum = parseFloat(val) || 0;
            if (commercialSaleableTotal > 0) {
              const officesPct = Math.min(100, Math.max(0, (areaNum / commercialSaleableTotal) * 100));
              const shopsPct = 100 - officesPct;
              setFormData({
                ...formData,
                saleableAreaSplitOffices: officesPct.toFixed(2),
                saleableAreaSplitShops: shopsPct.toFixed(2),
              });
            }
          };
          const handleShopsAreaChange = (val) => {
            const areaNum = parseFloat(val) || 0;
            if (commercialSaleableTotal > 0) {
              const shopsPct = Math.min(100, Math.max(0, (areaNum / commercialSaleableTotal) * 100));
              const officesPct = 100 - shopsPct;
              setFormData({
                ...formData,
                saleableAreaSplitOffices: officesPct.toFixed(2),
                saleableAreaSplitShops: shopsPct.toFixed(2),
              });
            }
          };
          const fmt = (n) => Math.round(n).toLocaleString('en-IN');

          return (
            <div className="space-y-3 p-3 border border-blue-200 bg-blue-50/40 rounded-sm" data-testid="saleable-split-block">
              <Label className="text-sm font-semibold">
                Commercial Saleable Area Split — Offices vs Shops
              </Label>
              <p className="text-[11px] text-muted-foreground">
                {formData.projectType === "mixed"
                  ? `Flats get ${formData.residentialPercentage || 0}% (Residential). Split the remaining ${formData.commercialPercentage || 0}% Commercial between Offices and Shops. Offices + Shops must total 100%.`
                  : "Split the full commercial saleable area between Offices and Shops. They must total 100%."}
              </p>

              {/* Two-way input: % or area (whichever the user prefers) */}
              <div className="grid grid-cols-2 gap-3">
                {/* Offices */}
                <div className="space-y-2 p-2 bg-white/60 rounded-sm">
                  <Label className="text-xs font-semibold">Offices</Label>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <Label htmlFor="q-saleableAreaSplitOffices" className="text-[10px] text-muted-foreground">%</Label>
                      <Input
                        id="q-saleableAreaSplitOffices"
                        name="saleableAreaSplitOffices"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.saleableAreaSplitOffices || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          const num = Math.min(100, Math.max(0, parseFloat(val) || 0));
                          setFormData({
                            ...formData,
                            saleableAreaSplitOffices: val,
                            saleableAreaSplitShops: String((100 - num).toFixed(2)).replace(/\.?0+$/, ""),
                          });
                        }}
                        placeholder={formData.projectType === "commercial" ? "40" : "50"}
                        className="rounded-sm h-8 text-sm"
                        data-testid="quick-saleable-split-offices"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-officesArea" className="text-[10px] text-muted-foreground">Area (sft)</Label>
                      <Input
                        id="q-officesArea"
                        type="number"
                        min="0"
                        value={commercialSaleableTotal > 0 ? Math.round(officesArea) : ""}
                        onChange={(e) => handleOfficesAreaChange(e.target.value)}
                        placeholder={commercialSaleableTotal > 0 ? fmt(commercialSaleableTotal * 0.4) : "—"}
                        disabled={commercialSaleableTotal === 0}
                        className="rounded-sm h-8 text-sm"
                        data-testid="quick-offices-area"
                      />
                    </div>
                  </div>
                </div>

                {/* Shops */}
                <div className="space-y-2 p-2 bg-white/60 rounded-sm">
                  <Label className="text-xs font-semibold">Shops</Label>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <Label htmlFor="q-saleableAreaSplitShops" className="text-[10px] text-muted-foreground">%</Label>
                      <Input
                        id="q-saleableAreaSplitShops"
                        name="saleableAreaSplitShops"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.saleableAreaSplitShops || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          const num = Math.min(100, Math.max(0, parseFloat(val) || 0));
                          setFormData({
                            ...formData,
                            saleableAreaSplitShops: val,
                            saleableAreaSplitOffices: String((100 - num).toFixed(2)).replace(/\.?0+$/, ""),
                          });
                        }}
                        placeholder={formData.projectType === "commercial" ? "60" : "50"}
                        className="rounded-sm h-8 text-sm"
                        data-testid="quick-saleable-split-shops"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-shopsArea" className="text-[10px] text-muted-foreground">Area (sft)</Label>
                      <Input
                        id="q-shopsArea"
                        type="number"
                        min="0"
                        value={commercialSaleableTotal > 0 ? Math.round(shopsArea) : ""}
                        onChange={(e) => handleShopsAreaChange(e.target.value)}
                        placeholder={commercialSaleableTotal > 0 ? fmt(commercialSaleableTotal * 0.6) : "—"}
                        disabled={commercialSaleableTotal === 0}
                        className="rounded-sm h-8 text-sm"
                        data-testid="quick-shops-area"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`text-xs p-2 rounded-sm flex items-center justify-between ${
                  isEmpty ? 'bg-secondary text-muted-foreground'
                  : isValid ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
                }`}
                data-testid="saleable-split-status"
              >
                <span>
                  Total: {commercialSplit.toFixed(2).replace(/\.?0+$/, "")}%{" "}
                  {isValid && "✓"}
                  {!isValid && !isEmpty && "— Offices + Shops must equal 100%"}
                </span>
                {commercialSaleableTotal > 0 && (
                  <span className="text-[11px] font-mono">
                    Total commercial: {fmt(commercialSaleableTotal)} sft
                  </span>
                )}
              </div>
              {commercialSaleableTotal === 0 && (
                <p className="text-[11px] text-muted-foreground italic">
                  Area preview appears after you fill in Plot Area, Road Width, and other calc inputs (any tab).
                </p>
              )}
            </div>
          );
        })()}

        {/* C2 Zone (Commercial / Mixed Use only) */}
        {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
          <div className="space-y-2 p-3 border border-amber-200 bg-amber-50/40 rounded-sm">
            <Label className="text-sm font-semibold">C2 Zone?</Label>
            <Select
              value={formData.isC2Zone || "no"}
              onValueChange={(value) => setFormData({ ...formData, isC2Zone: value })}
            >
              <SelectTrigger className="rounded-sm" data-testid="quick-is-c2-zone">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-amber-800">
              C2 zone adds: <strong>conversion charges</strong> (40% of plot × land RR) +
              <strong> additional construction</strong> (50% of plot area).
            </p>
            {formData.isC2Zone === "yes" && (
              <div className="space-y-1 pt-2">
                <Label className="text-xs">C2 Additional Construction Cost (₹/sft) *</Label>
                <Input
                  type="number"
                  value={formData.c2AdditionalConstructionRate || ""}
                  onChange={(e) => setFormData({ ...formData, c2AdditionalConstructionRate: e.target.value })}
                  placeholder="e.g. 3200"
                  className="rounded-sm"
                  data-testid="quick-c2-construction-rate"
                />
                <p className="text-[11px] text-muted-foreground">
                  Rate per sft for the additional 50% of plot area construction required by C2 zone.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Number of Lifts (for Lift Machine Room cost) */}
        <div className="space-y-2">
          <Label>Number of Lifts</Label>
          <Input
            type="number"
            value={formData.numberOfLifts || ""}
            onChange={(e) => setFormData({ ...formData, numberOfLifts: e.target.value })}
            placeholder="e.g. 4"
            className="rounded-sm"
            data-testid="quick-number-of-lifts"
          />
          <p className="text-[11px] text-muted-foreground">
            Adds Lift Machine Room cost = lifts × 8 sqm × 60% × Land RR.
          </p>
        </div>
        
        {/* Green Rating */}
        <div className="space-y-2">
          <Label>Green Rating</Label>
          <Select
            value={formData.greenRatingRequired === "true" ? formData.greenFSICategory || "none" : "none"}
            onValueChange={(value) => {
              if (value === "none") {
                setFormData({ ...formData, greenRatingRequired: "false", greenFSICategory: "none" });
              } else {
                setFormData({ ...formData, greenRatingRequired: "true", greenFSICategory: value });
              }
            }}
          >
            <SelectTrigger className="rounded-sm" data-testid="quick-green-rating">
              <SelectValue placeholder="Select green rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="silver">Silver (3%)</SelectItem>
              <SelectItem value="gold">Gold (5%)</SelectItem>
              <SelectItem value="platinum">Platinum (7%)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">% of Basic FSI (1.1)</p>
        </div>
        
        {/* Base FSI Display */}
        {parseFloat(formData.roadWidth) > 0 && formData.projectClassification && (
          <div className="lg:col-span-3 md:col-span-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="base-fsi-display">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Base FSI (Auto-calculated)</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-bold text-blue-900 font-mono">
                    {formData.projectClassification === "mhada" 
                      ? "3.00" 
                      : fsiBreakdown?.permissibleFSI?.toFixed(2) || (
                          (() => {
                            const roadWidth = parseFloat(formData.roadWidth) || 0;
                            const classification = formData.projectClassification;
                            if (roadWidth < 9) return "—";
                            if (classification === "tod") {
                              if (roadWidth >= 24) return "4.00";
                              if (roadWidth >= 15) return "3.50";
                              if (roadWidth >= 12) return "3.00";
                              return "2.50";
                            } else {
                              if (roadWidth >= 30) return "3.00";
                              if (roadWidth >= 24) return "2.75";
                              if (roadWidth >= 12) return "2.50";
                              return "2.00";
                            }
                          })()
                        )
                    }
                  </span>
                  <span className="text-xs text-blue-600 uppercase tracking-wide">
                    {formData.projectClassification === "mhada" ? "MHADA" : formData.projectClassification?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
